import React, { useEffect, useRef, useState } from "react";
import { Intent } from "@blueprintjs/core";
import { Button } from "components/buttons";
import { DateToLocal } from "components/dates";
import { Filter, FilterButton } from "components/filtering";
import {
    FormLocationSelector,
    FormTextInput,
    FormToggleList,
} from "components/form-fields";
import { ListingTable } from "components/listing";
import {
    NotificationInline,
    NotificationToaster,
} from "components/notifications";
import { EmployeeStatus } from "components/status";
import { Tooltip } from "components/tooltip";
import { UserHasAccess } from "components/user-access";
import { useDebounce } from "hooks/useDebounce";
import { useQuery } from "hooks/useQuery";
import { LicenceCheckingLicenceConflictModal } from "pages/licence-checking/shared/licence-conflict";
import { LicenceCheckingNewConsentModal } from "pages/licence-checking/shared/new-consent";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { EmployeeService, LicenceCheckingService } from "services";
import { Dropdown } from "components/dropdown";
import { InstantReportButton } from "components/reporting";
import axios from "axios";

const ON_SITE_ELIGIBLE_STATUSES = new Set([
    "AWAITINGCONSENT",
    "REQUIRESEMAIL",
    "NOCONSENT",
    "EXPIREDCONSENT",
    "REFUSEDCONSENT",
    "NODVLARECORD",
]);

function shouldShowOnSiteAction(consentStatus, invalidLicence) {
    if (consentStatus === "CONFLICTINGDETAILS") {
        return invalidLicence === true;
    }
    return ON_SITE_ELIGIBLE_STATUSES.has(consentStatus);
}

export function LicenceCheckingDriverOnboarding() {
    const debounceTimeout = 750;

    const [initalSetupComplete, setInitalSetupComplete] = useState(false);

    const [isLoading, setIsLoading] = useState(true);

    const reduxLocations = useSelector(
        (state) => state.licenceChecking.locations
    );

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const query = useQuery();
    const [useHierarchy, setUseHierarchy] = useState(true);
    const [useLocationDefaults, setUseLocationDefaults] = useState(null);

    const [showFilters, setShowFilters] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState([]);
    const [pageFilters, setPageFilters] = useState([]);

    const tableHeaders = [
        "Employee Name",
        "Email",
        "Location",
        "Date of Last Email",
        "Status",
        "Action",
    ];
    const sortableHeaders = [
        { headerName: "Employee Name", sortName: "EMPLOYEE.NAME" },
        { headerName: "Email", sortName: "EMAILADDRESS" },
        { headerName: "Location", sortName: "EMPLOYEE.LOCATION" },
        { headerName: "Status", sortName: "STATUS" },
    ];

    const [sortedBy, setSortedBy] = useState(sortableHeaders[0]);
    const [sortedDir, setSortedDir] = useState("A");

    const [totalRecords, setTotalRecords] = useState(0);
    const [tableRows, setTableRows] = useState([]);
    const [pageNumber, setPageNumber] = useState(1);

    const [statusOptions, setStatusOptions] = useState([]);
    const [employeeTypeOptions, setEmployeeTypeOptions] = useState([]);
    const [employeeStatusOptions, setEmployeeStatusOptions] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [selectedStatus, setSelectedStatus] = useState(query.get("filter"));

    const currentPageNumber = useRef();
    const currentTableSortBy = useRef();
    const currentTableSortDir = useRef();
    const currentAppliedFilters = useRef();
    const currentLocations = useRef();
    const currentStatus = useRef();
    const currentSearchTerm = useRef();

    const requiredAction = "LicenceChecking:View";
    const canAccessPage = UserHasAccess(requiredAction);

    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebounce(searchTerm, debounceTimeout);

    const [showConflictModal, setShowConflictModal] = useState(false);
    const [showConsentModal, setShowConsentModal] = useState(false);

    const [selectedEmployee, setSelectedEmployee] = useState({});

    // Reports
    const [showReportList, setShowReportList] = useState(false);
    const [reportList, setReportList] = useState([]);
    const [reportGenerationId, setReportGenerationId] = useState(null);

    function onPagingChange(newPageNumber) {
        setPageNumber(newPageNumber);
    }

    function onFilterChange(listingFilters) {
        setAppliedFilters(listingFilters);
    }

    function onToggleFilter() {
        setShowFilters((prevState) => {
            return !prevState;
        });
    }

    function onTableSort(header, direction) {
        setSortedDir(direction);
        setSortedBy(header);
    }

    function onSearchChange(item) {
        setSearchTerm(item.target.value);
    }

    function onSelectedLocationsChange(locations) {
        dispatch({
            type: "SET_LICENCE_CHECKING_LOCATIONS",
            payload: locations,
        });
    }

    function onStatusChange(key) {
        setSelectedStatus(key);
    }

    useEffect(() => {
        dispatch({ type: "SITE_FULL_WIDTH" });
    }, []);

    useEffect(() => {
        if (reduxLocations && reduxLocations.length > 0) {
            setUseLocationDefaults(false);
        } else {
            setUseLocationDefaults(true);
        }
    }, [reduxLocations]);

    useEffect(
        function () {
            setReportList([
                <InstantReportButton
                    buttonType="MenuItem"
                    icon={<i className="fa fa-file-excel-o"></i>}
                    key={"onboarding-report"}
                    text={"Licence Checking Onboarding"}
                    onReportGenerate={generateOnboardingReport}
                    onReportDownloadComplete={generateOnboardingReportComplete}
                    reportId={reportGenerationId}
                />,
            ]);

            setTimeout(() => {
                setShowReportList(false);
            });
        },
        [reportGenerationId]
    );

    function fetchTableData(forceRefresh) {
        if (!reduxLocations) {
            setTableRows([]);
            setTotalRecords(0);
            setIsLoading(false);
            return;
        }

        if (canAccessPage && initalSetupComplete) {
            if (
                currentPageNumber.current === pageNumber &&
                currentTableSortBy.current === sortedBy &&
                currentTableSortDir.current === sortedDir &&
                currentSearchTerm.current === debouncedSearchTerm &&
                currentAppliedFilters.current === appliedFilters &&
                currentLocations.current === reduxLocations.length &&
                currentStatus.current === selectedStatus &&
                !forceRefresh
            ) {
                //If none of the values have changed, then 2 have been updated at once, so prevent the listing from performing another call.
                return;
            }

            currentPageNumber.current = pageNumber;
            currentTableSortBy.current = sortedBy;
            currentTableSortDir.current = sortedDir;
            currentSearchTerm.current = debouncedSearchTerm;
            currentAppliedFilters.current = appliedFilters;
            currentLocations.current = reduxLocations.length;
            currentStatus.current = selectedStatus;

            setIsLoading(true);

            const mappedFilters = mapFilters();

            const pageSize = 50;

            const searchRequest = {
                requestCount: pageSize,
                pageNumber: pageNumber,
                sortField: sortedBy.sortName,
                sortDirection: sortedDir,
                filters: mappedFilters,
                searchTerm: debouncedSearchTerm,
            };

            LicenceCheckingService.getOnboardingListing(searchRequest).then(
                (r) => {
                    let data = [];

                    if (r?.data != null) {
                        data = r.data.map((d) => {
                            return [
                                <div
                                    key={d.employee.id}
                                    className="inline-items"
                                >
                                    <EmployeeStatus
                                        statusId={d.employee.statusId}
                                        typeId={d.employee.typeId}
                                        typeAttributeMappings={
                                            employeeTypeOptions
                                        }
                                        statusAttributeMappings={
                                            employeeStatusOptions
                                        }
                                    />
                                    <Link
                                        to={`/employee/${d.employee.id}/licence-checking`}
                                    >
                                        {d.employee.name}
                                    </Link>
                                </div>,
                                d.emailAddress,
                                d.employee.location,
                                <DateToLocal
                                    key={d.employee.id}
                                    format="DD/MM/YYYY"
                                >
                                    {d.lastEmailSent}
                                </DateToLocal>,
                                renderStatus(d.status, d.invalidLicence),
                                <div className="button-row-small" key={d.employee.id}>
                                    {renderConsentActionButton(d.status, d.invalidLicence, d)}
                                    {renderOnSiteActionButton(d.status, d.invalidLicence, d)}
                                </div>,
                            ];
                        });
                    }

                    setTableRows(data);
                    setTotalRecords(r.totalCount);
                    setIsLoading(false);

                    setStatuses(function () {
                        return statusOptions.map((status) => {
                            var statusCount = r.onboardingStatusCounts.find(
                                (s) => s.id === status.id
                            );

                            return {
                                ...status,
                                count: statusCount?.count ?? null,
                            };
                        });
                    });
                },
                function () {
                    NotificationToaster.show(
                        Intent.DANGER,
                        "Could not retrieve onboarding records, please try again"
                    );
                }
            );
        }
    }

    useEffect(() => {
        fetchTableData(false);
    }, [
        pageNumber,
        sortedBy,
        sortedDir,
        debouncedSearchTerm,
        appliedFilters,
        initalSetupComplete,
        selectedStatus,
    ]);

    useEffect(() => {
        axios
            .all([
                EmployeeService.getEmployeeOptions(),
                LicenceCheckingService.getLicenceCheckingOptions(),
            ])
            .then(function ([employeeOptions, licenceCheckingOptions]) {
                setStatusOptions(licenceCheckingOptions.onboardingStatuses);
                setEmployeeStatusOptions(employeeOptions.employeeStatuses);
                setEmployeeTypeOptions(employeeOptions.employeeTypes);

                setPageFilters([
                    {
                        name: "EMPLOYEESTATUS",
                        displayName: "Status",
                        items: employeeOptions.employeeStatuses.filter(
                            (status) => {
                                return status.active;
                            }
                        ),
                    },
                    {
                        name: "EMPLOYEETYPE",
                        displayName: "Type",
                        items: employeeOptions.employeeTypes,
                    },
                    {
                        name: "EMPLOYEECATEGORY",
                        displayName: "Category",
                        items: employeeOptions.employeeCategories,
                    },
                ]);
                setInitalSetupComplete(true);
            });
    }, []);

    function mapFilters() {
        let mappedFilters = (currentAppliedFilters.current ?? []).map(function (f) {
            return {
                key: f.name,
                ...f,
            };
        });

        if (reduxLocations != null) {
            const mappedLocations = reduxLocations.map(function (l) {
                return { key: "Location", value: l };
            });

            mappedFilters = [...mappedFilters, ...mappedLocations];
        }

        if (currentStatus.current != null) {
            mappedFilters = [
                ...mappedFilters,
                {
                    key: "STATUS",
                    value: currentStatus.current,
                },
            ];
        }

        return mappedFilters;
    }

    function onActionButtonClick(employeeToAction, selectedAction) {
        setSelectedEmployee(employeeToAction);
        selectedAction();
    }

    function renderIconActionButton(icon, tooltip, onClick) {
        return (
            <Tooltip content={tooltip}>
                <Button
                    icon={icon}
                    iconOnly
                    minimal
                    large={false}
                    onClick={onClick}
                />
            </Tooltip>
        );
    }

    function onPerformOnSiteClick(employeeId) {
        navigate(`/licence-checking/declaration/on-site/${employeeId}`, {
            state: {
                returnTo: `/employee/${employeeId}/licence-checking`,
                cancelTo: "/licence-checking/employee-onboarding",
            },
        });
    }

    function renderConsentActionButton(consentStatus, invalidLicence, employee) {
        let icon = "repeat";
        let buttonText = "Restart Declaration";
        let buttonAction = onConsentModalOpen;

        switch (consentStatus) {
            case "CONFLICTINGDETAILS":
                if (invalidLicence !== true) {
                    icon = "error";
                    buttonText = "Compare Conflicts";
                    buttonAction = onConflictModalOpen;
                }
                break;
            case "AWAITINGCONSENT":
                icon = "envelope";
                buttonText = "Resend Declaration Email";
                break;
            case "REQUIRESEMAIL":
                icon = "plus";
                buttonText = "Add Email Address";
                break;
            case "NOCONSENT":
                icon = "plus";
                buttonText = "Enroll in Licence Checking";
                break;
        }

        return renderIconActionButton(icon, buttonText, () => {
            onActionButtonClick(employee, buttonAction);
        });
    }

    function renderOnSiteActionButton(consentStatus, invalidLicence, employee) {
        if (!shouldShowOnSiteAction(consentStatus, invalidLicence)) {
            return null;
        }
        return renderIconActionButton(
            "person",
            "Perform declaration on site",
            () => onPerformOnSiteClick(employee.employee.id)
        );
    }

    function renderStatus(statusKey, invalidLicence) {
        if (invalidLicence === true && statusKey === "CONFLICTINGDETAILS") {
            return "Declaration details do not match with the licence issuing authority";
        }
        return (
            statusOptions.find((s) => s.key === statusKey)?.name ?? "Unknown"
        );
    }

    function onConflictModalOpen() {
        setShowConflictModal(true);
    }

    function onConflictModalClose() {
        setShowConflictModal(false);
    }

    function onConsentModalOpen() {
        setShowConsentModal(true);
    }

    function onConsentModalClose() {
        setShowConsentModal(false);
    }

    function onModalSuccess() {
        NotificationToaster.show(
            Intent.SUCCESS,
            "Declaration has been updated successfully"
        );
        fetchTableData(true);
    }

    function onReportDropdownClick() {
        setShowReportList((prev) => !prev);
    }

    function generateOnboardingReport() {
        LicenceCheckingService.generateOnboardingReport(mapFilters())
            .then(
                (response) => {
                    setReportGenerationId(response);
                },
                () => {
                    setReportGenerationId(null);
                    NotificationToaster.show(
                        Intent.DANGER,
                        "Failed to generate report. Please try again."
                    );
                }
            )
            .catch(() => {
                NotificationToaster.show(
                    Intent.DANGER,
                    "Failed to generate report. Please try again."
                );
            });
    }

    function generateOnboardingReportComplete() {
        setReportGenerationId(null);
    }

    return (
        <>
            <div className="filter-controls">
                <div className="inline-items spacer-bottom ">            
                    <div className="inline-item fill">
                        <div className="form-field-full">
                            <FormTextInput
                                placeholder="Search Employees"
                                onChange={onSearchChange}
                                value={searchTerm}
                                large
                                disabled={isLoading}
                                icon="search"
                                id="onboarding-search-field"
                            />
                        </div>
                    </div>
                    <div className="inline-item">
                        <FormLocationSelector
                            businessArea={"Tacho"}
                            loading={!initalSetupComplete}
                            selectedLocations={reduxLocations}
                            setSelectedLocations={onSelectedLocationsChange}
                            onLocationListClose={fetchTableData}
                            useLocationDefaults={useLocationDefaults}
                            useHierarchy={useHierarchy}
                            setUseHierarchy={setUseHierarchy}
                            locationDefaultsToTrue={isLoading ? null : true}
                            disabled={isLoading}
                        />
                    </div>                    
                    <div className="inline-item">
                        <Dropdown
                            isOpen={showReportList}
                            onClick={onReportDropdownClick}
                            intent="primary"
                            text={""}
                            icon={"document"}
                            large
                            iconOnly
                            id="reports"
                            items={reportList}
                            preamble={
                                <div>
                                    <h4>Downloads</h4>
                                    <p>
                                        Filters set on this page will be applied to
                                        the report when it is generated. Please make
                                        sure the data on the screen represents what
                                        you want to see within your report.
                                    </p>
                                </div>
                            }
                            disabled={false}
                        />
                    </div>  
                    <div className="inline-item">
                        <FilterButton onToggle={onToggleFilter} filters={appliedFilters} />
                    </div>                  
                </div>
                <Filter
                    filterName={"licenceCheckingOnboarding"}
                    visible={showFilters}
                    filters={pageFilters}
                    onUpdate={onFilterChange}
                    onClickOutside={onToggleFilter}
                />
            </div>
            <NotificationInline
                intent="info"
                text="Your report has been queued, and will download as soon as it's ready."
                show={reportGenerationId != null}
            />
            <div className="spacer-bottom">
                <FormToggleList
                    disabled={isLoading}
                    items={statuses}
                    value={selectedStatus}
                    onChange={onStatusChange}
                    className="restricted-width"
                />
            </div>
            <ListingTable
                showPerPage={false}
                pageSize={50}
                headers={tableHeaders}
                data={tableRows}
                totalRecordCount={totalRecords}
                loadingData={isLoading}
                onPagingChange={onPagingChange}
                sortable
                sortableHeaders={sortableHeaders}
                sortedBy={sortedBy}
                sortedDir={sortedDir}
                onSort={onTableSort}
            />

            <LicenceCheckingNewConsentModal
                key={selectedEmployee?.employee?.id}
                isOpen={showConsentModal}
                employeeId={selectedEmployee?.employee?.id}
                emailAddress={
                    selectedEmployee?.employee?.emailAddress ??
                    selectedEmployee?.emailAddress
                }
                employeeName={selectedEmployee?.employee?.name}
                onSuccess={onModalSuccess}
                onClose={onConsentModalClose}
                consentExists={
                    selectedEmployee?.consentId != null &&
                    selectedEmployee?.emailAddress != null
                }
            />
            <LicenceCheckingLicenceConflictModal
                key={selectedEmployee?.employee?.id}
                isOpen={showConflictModal}
                existingLicenceNumber={
                    selectedEmployee?.employee?.licenceNumber
                }
                consentLicenceNumber={selectedEmployee?.licenceNumber}
                employeeName={selectedEmployee?.employee?.name}
                onClose={onConflictModalClose}
                onSuccess={onModalSuccess}
                employeeId={selectedEmployee?.employee?.id}
                emailAddress={selectedEmployee?.emailAddress}
                consentId={selectedEmployee?.consentId}
                licenceNumberOverridable={
                    selectedEmployee?.employee?.canCopyLicenceNumber
                }
            />
        </>
    );
}
